import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AdminApi, ApiError } from "@/api";
import {
  emptyTraceNotice,
  traceErrorNotice,
  TraceView,
} from "@/components/llm-trace";
import { Banner, Card, CardHead } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { AdminSessionMessage, ProactiveSessionItem } from "@/types";
import type { PromptBindingName } from "@/types/llmTrace";
import { mapValue, stringValue } from "@/utils/coerce";
import { compactTime } from "@/utils/format";
import { sessionTypeLabel } from "@/utils/sessionType";
import type { NormalizedTrace } from "@/utils/trace/model";
import { fromLlmTrace } from "@/utils/trace/normalize";
import {
  ConversationSearchBar,
  type LookupNotice,
  LookupNoticeCard,
} from "./ConversationSearchBar";
import { ConversationSessionList, SESSION_LIST_LIMIT } from "./ConversationSessionList";
import { ConversationThread } from "./ConversationThread";
import {
  CONVERSATIONS_ROUTE,
  type ConversationsSearch,
  type TraceEnvironment,
} from "./conversationsSearch";
import { MessageInspector } from "./MessageInspector";
import {
  type RecentLookup,
  isSessionId,
  loadRecentLookups,
  rememberLookup,
  resolveSessionQuery,
  saveRecentLookups,
} from "./sessionLookup";

const PAGE_SIZE = 100;

/** One answered request, tagged with the request it answers. */
type LoadedSession = {
  key: string;
  session: Record<string, unknown>;
  messages: AdminSessionMessage[];
  nextBeforeSeq: number | null;
};
type FailedSession = { key: string; code: string; message: string };
type TraceResult = { key: string; trace?: NormalizedTrace; error?: string };
type LoadedList = { key: string; items: ProactiveSessionItem[] };
type FailedList = { key: string; message: string };

// Stable empties, so memos keyed on them do not recompute every render.
const NO_MESSAGES: AdminSessionMessage[] = [];
const NO_SESSION: Record<string, unknown> = {};
const NO_SESSIONS: ProactiveSessionItem[] = [];

function errorText(err: unknown) {
  // Keep the code: `traceErrorNotice` matches on it, and the four documented
  // failures each imply a different next move.
  if (err instanceof ApiError && err.code) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Conversations: find a session, read it, and open the trace of any answer in
 * it (fe/CONVERSATIONS.md).
 *
 * Without `?session` the screen is the session list — most recently active
 * first, narrowed by `?q` and `?email`. A pasted `session_id` skips the list.
 * The search bar stays at the top of an open conversation too, so the next
 * one is a search away rather than a trip back.
 *
 * **The URL is the state.** `?env&session&message` name one answer; arriving
 * with them set is the same code path as clicking, which is what makes the TC
 * drawer's "원본 대화 Trace" a link rather than an instruction.
 *
 * With no `message`, no answer is open and the conversation has the width to
 * itself. An answer's "Trace 보기" opens its trace beside it.
 *
 * The trace is the same `TraceView` Test Result renders, fed through
 * `fromLlmTrace`, so the two stay identical by construction (§4).
 */
export function ConversationsPage({
  apiBaseUrl,
  bearerToken,
  samsApiBaseUrl,
}: {
  apiBaseUrl: string;
  bearerToken: string;
  samsApiBaseUrl?: string;
}) {
  const search = useSearch({ from: CONVERSATIONS_ROUTE });
  const evaluationRuntime = search.store === "sams";
  const api = useMemo(
    () => {
      const client = AdminApi.forSamsLogin(apiBaseUrl, bearerToken, apiBaseUrl, samsApiBaseUrl || undefined);
      return evaluationRuntime ? client.forEvaluationRuntime() : client;
    },
    [apiBaseUrl, bearerToken, samsApiBaseUrl, evaluationRuntime],
  );
  const navigate = useNavigate();
  const environment = search.env;
  // A session param that is not a UUID is never sent: the server would 422,
  // and the list screen can say what is wrong with it instead.
  const sessionId = isSessionId(search.session) ? search.session : null;
  // Which step of the selected answer's trace has its detail open. In the URL
  // so back closes it — see `conversationsSearch`.
  const activeNodeId = search.node;

  const [recents, setRecents] = useState<RecentLookup[]>(loadRecentLookups);
  // Bumped by a lookup so re-submitting the id already in the URL retries it.
  const [attempt, setAttempt] = useState(0);
  // Bumped by 조회 so the same search, submitted again, is asked again.
  const [listAttempt, setListAttempt] = useState(0);

  // Every result is stored with the request it answers and read back through
  // that key, so nothing from a previous session — its error, its empty list —
  // can paint as this one's in the frame before the next effect runs. Loading
  // is then derived: a key with no answer yet.
  const ownerKey = evaluationRuntime ? "sams" : environment;
  const sessionKey = sessionId ? `${ownerKey}:${sessionId}:${attempt}` : null;
  const [loaded, setLoaded] = useState<LoadedSession | null>(null);
  const [failed, setFailed] = useState<FailedSession | null>(null);
  const current = loaded && loaded.key === sessionKey ? loaded : null;
  const sessionError = failed && failed.key === sessionKey ? failed : null;
  const messagesLoading = sessionKey !== null && !current && !sessionError;
  const session = current?.session ?? NO_SESSION;
  const messages = current?.messages ?? NO_MESSAGES;
  const nextBeforeSeq = current?.nextBeforeSeq ?? null;

  const [olderLoading, setOlderLoading] = useState(false);
  const [olderError, setOlderError] = useState("");

  const [traceResult, setTraceResult] = useState<TraceResult | null>(null);

  // The list, keyed the same way. It is shown with no session, and under the
  // notice when the addressed one did not open.
  const showList = !sessionId || sessionError !== null;
  const listKey = `${ownerKey}:${search.q ?? ""}:${search.email ?? ""}:${search.purpose ?? "all"}:${listAttempt}`;
  const [list, setList] = useState<LoadedList | null>(null);
  const [listFailed, setListFailed] = useState<FailedList | null>(null);
  const sessions = list && list.key === listKey ? list.items : NO_SESSIONS;
  const listError = listFailed && listFailed.key === listKey ? listFailed.message : "";
  const listLoading = showList && list?.key !== listKey && !listError;

  /**
   * `env:session` of the conversation last opened — what "a different
   * conversation" is measured against, both below and in `updateSearch`.
   */
  const lastOpened = useRef<string | null>(null);

  /**
   * Navigate within the screen.
   *
   * **Opening a different conversation drops the open answer and its node**
   * in the same navigation. A message id means something only inside the
   * session that holds it; carried across it would ask for a trace that
   * cannot exist, and split the page around it for the frames until the
   * guard below caught up. Every entrance on this screen — a row, a pasted
   * id, a recent chip — comes through here, so none of them has to remember.
   *
   * **The list is a pause, not a change.** Only a non-null session is
   * compared, so going back to the list keeps `message`, and opening the
   * conversation that was open returns the reader to the answer they were on.
   *
   * **The page stays where it is unless the view changes.** The router
   * scrolls the window to the top after every navigation by default, and
   * most navigations here are the page's own state — a trace step, an
   * answer's trace — so each click on a bar threw the reader to the top as
   * if the page had reloaded. Only another conversation, or the list, starts
   * at the top.
   */
  const updateSearch = useCallback(
    (patch: Partial<ConversationsSearch>, opts: { replace?: boolean } = {}) => {
      const next = { ...search, ...patch };
      if (
        patch.session &&
        lastOpened.current !== null &&
        `${next.store === "sams" ? "sams" : next.env}:${patch.session}` !== lastOpened.current
      ) {
        next.message = null;
        next.node = null;
      }
      navigate({
        to: CONVERSATIONS_ROUTE,
        search: next,
        replace: opts.replace,
        resetScroll: next.session !== search.session,
      });
    },
    [navigate, search],
  );

  /**
   * The same rule for entrances that do not come through `updateSearch` —
   * a hand-edited address, back and forward.
   *
   * Keyed by environment too: the same id in the other database is a
   * different conversation, and its message ids do not carry.
   *
   * The FIRST conversation is exempt — arriving with `?session` and
   * `?message` set is the deep link this screen exists for.
   */
  useEffect(() => {
    if (!sessionId) return;
    const key = `${ownerKey}:${sessionId}`;
    const previous = lastOpened.current;
    lastOpened.current = key;
    if (previous && previous !== key && (search.message || search.node)) {
      updateSearch({ message: null, node: null }, { replace: true });
    }
  }, [sessionId, ownerKey, search.message, search.node, updateSearch]);

  const remember = useCallback((entry: RecentLookup) => {
    setRecents((current) => {
      const next = rememberLookup(current, entry);
      saveRecentLookups(next);
      return next;
    });
  }, []);

  // The session list follows `?env&q&email`. Coming back from a conversation
  // to the same search shows what was there rather than asking again; 조회
  // is what refreshes it.
  const listLoadedKey = list?.key;
  useEffect(() => {
    if (!showList || listLoadedKey === listKey) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.searchProactiveSessions({
          q: search.q ?? "",
          targetUserEmail: search.email ?? "",
          runPurpose: search.purpose ?? "all",
          limit: SESSION_LIST_LIMIT,
          environment,
        });
        if (!cancelled) setList({ key: listKey, items: response.items || [] });
      } catch (err) {
        if (!cancelled) setListFailed({ key: listKey, message: errorText(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, showList, listKey, listLoadedKey, search.q, search.email, search.purpose, environment]);

  // The session's messages follow `?session`.
  useEffect(() => {
    if (!sessionId || !sessionKey) return;
    let cancelled = false;
    setOlderError("");
    void (async () => {
      try {
        const response = await api.listSessionMessages(sessionId, {
          limit: PAGE_SIZE,
          environment,
        });
        if (cancelled) return;
        setLoaded({
          key: sessionKey,
          session: mapValue(response.session),
          messages: response.messages || [],
          nextBeforeSeq: response.next_before_seq,
        });
        // Remembered only once it opened: a chip must lead somewhere.
        if (!evaluationRuntime) remember({ id: sessionId, env: environment });
      } catch (err) {
        if (cancelled) return;
        setFailed({
          key: sessionKey,
          code: err instanceof ApiError ? err.code : "",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, sessionId, environment, sessionKey, remember, evaluationRuntime]);

  const assistantIds = useMemo(
    () => messages.filter((m) => m.role === "assistant").map((m) => m.chat_message_id),
    [messages],
  );
  // Only what was picked — there is no fallback to the latest answer. An
  // answer that is always open has no "closed" to return to, and the reader
  // could never have the conversation to themselves.
  const selectedId = search.message;
  const selectedMessage = useMemo(
    () => messages.find((m) => m.chat_message_id === selectedId) ?? null,
    [messages, selectedId],
  );

  // …and the trace follows the selected answer, keyed the same way.
  const traceKey =
    sessionId && selectedId && !sessionError ? `${ownerKey}:${sessionId}:${selectedId}` : null;
  const traceFor = traceResult && traceResult.key === traceKey ? traceResult : null;
  const trace = traceFor?.trace;
  const traceError = traceFor?.error ?? "";
  const traceLoading = traceKey !== null && !traceFor;

  useEffect(() => {
    if (!sessionId || !selectedId || !traceKey) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.getLlmTrace(sessionId, selectedId, environment);
        if (!cancelled) setTraceResult({ key: traceKey, trace: fromLlmTrace(response) });
      } catch (err) {
        if (!cancelled) setTraceResult({ key: traceKey, error: errorText(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, sessionId, selectedId, environment, traceKey]);

  // While the page is one column the trace sits under the conversation, so
  // opening one would otherwise change only something off screen. `nearest`
  // does nothing once the column is beside the answer, so on a split layout
  // the view stays put.
  const traceColumnRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!search.message) return;
    traceColumnRef.current?.scrollIntoView({ block: "nearest" });
  }, [search.message]);

  const loadOlder = async () => {
    if (!sessionId || !current || current.nextBeforeSeq === null) return;
    const { key, nextBeforeSeq: beforeSeq } = current;
    setOlderLoading(true);
    setOlderError("");
    try {
      const response = await api.listSessionMessages(sessionId, {
        limit: PAGE_SIZE,
        beforeSeq,
        environment,
      });
      // The server returns each page oldest-first, so an older page prepends.
      // Only onto the session it was asked for — the reader may have moved on.
      setLoaded((prev) =>
        prev && prev.key === key
          ? {
              ...prev,
              messages: [...(response.messages || []), ...prev.messages],
              nextBeforeSeq: response.next_before_seq,
            }
          : prev,
      );
    } catch (err) {
      setOlderError(errorText(err));
    } finally {
      setOlderLoading(false);
    }
  };

  const openSession = (id: string, env: TraceEnvironment = environment) => {
    setAttempt((n) => n + 1);
    updateSearch({ env, session: id });
  };

  /**
   * 조회. A session_id — whole, or the short form of a recent lookup — opens
   * that conversation and leaves the list's search as it was, so back returns
   * to it. Anything else, empty included, is a search of the list.
   */
  const submitSearch = (rawQ: string, rawEmail: string) => {
    const result = resolveSessionQuery(rawQ, evaluationRuntime ? [] : recents);
    if (result.kind === "full") return openSession(result.id);
    if (result.kind === "recent") return openSession(result.entry.id, result.entry.env);
    setListAttempt((n) => n + 1);
    updateSearch({
      q: rawQ.trim() || null,
      email: rawEmail.trim().toLowerCase() || null,
      session: null,
    });
  };

  const searchBar = (
    <div className="grid gap-2">
    {evaluationRuntime && <p className="text-sm font-medium">SAMS 보존 평가 대화</p>}
    <ConversationSearchBar
      variant={showList ? "list" : "detail"}
      environment={environment}
      q={search.q}
      email={search.email}
      session={search.session}
      recents={evaluationRuntime ? [] : recents}
      evaluationRuntime={evaluationRuntime}
      onEnvironment={(env: TraceEnvironment) => {
        if (env === environment) return;
        // Session ids do not carry across databases; the search does.
        updateSearch({ env, session: null, message: null, node: null });
      }}
      onSubmit={submitSearch}
      onPickRecent={(entry) => openSession(entry.id, entry.env)}
    />
    <label className="flex items-center gap-2 text-sm">대화 목적
      <select aria-label="대화 목적" value={search.purpose ?? "all"}
        onChange={(event) => updateSearch({ purpose: event.target.value as ConversationsSearch["purpose"], session: null, message: null, node: null })}
        className="rounded border bg-background p-2">
        <option value="all">전체</option><option value="production">운영</option>
        <option value="test">테스트</option><option value="reference">Reference</option>
        <option value="unknown">미분류</option>
      </select>
    </label>
    </div>
  );

  // --- List view -----------------------------------------------------------
  // Shown with no session, with one that is not a UUID, and with one that did
  // not load — the last two say why above the list rather than rendering an
  // empty shell.

  if (showList) {
    const notice: LookupNotice | null =
      search.session && !sessionId
        ? { kind: "invalid", input: search.session }
        : sessionError && sessionId
          ? {
              kind: "load_failed",
              input: sessionId,
              code: sessionError.code,
              message: sessionError.message,
            }
          : null;

    return (
      <div className="grid gap-4">
        {searchBar}
        {notice && (evaluationRuntime
          ? <Banner tone="error">{sessionError?.message || "대화 ID를 확인해 주세요."}</Banner>
          : <LookupNoticeCard environment={environment} notice={notice} />)}
        <ConversationSessionList
          items={sessions}
          loading={listLoading}
          error={listError}
          q={search.q}
          email={search.email}
          onOpen={(item) => openSession(item.session_id)}
        />
      </div>
    );
  }

  // --- Conversation view ---------------------------------------------------

  const displayName = stringValue(session.display_name, "");
  const sessionType = stringValue(session.session_type, "");
  const lastActive = stringValue(session.last_active_at, "") || stringValue(session.created_at, "");
  const position: [number, number] | null =
    selectedId && assistantIds.includes(selectedId)
      ? [assistantIds.indexOf(selectedId) + 1, assistantIds.length]
      : null;
  /** A linked answer that is not on the loaded page. */
  const orphan = Boolean(search.message) && !messagesLoading && !selectedMessage;

  /**
   * Two columns only while a trace is open. Until then the conversation is
   * all there is to read, and a column beside it would be empty.
   *
   * React decides whether two columns are WANTED; the container query in the
   * render decides whether there is room. Neither answer alone is enough.
   */
  const split = selectedId !== null;

  /**
   * Open an answer's trace, or close the one open.
   *
   * Opening is a push so back returns to the previous answer; closing is a
   * replace so back does not step through "same answer, trace closed".
   *
   * `node` goes with the answer either way: a node id is only meaningful in
   * the trace that holds it, and carrying one over would open a detail the
   * reader did not ask for — or, more often, nothing at all.
   */
  const select = (message: AdminSessionMessage) => {
    if (message.chat_message_id === search.message) {
      updateSearch({ message: null, node: null }, { replace: true });
      return;
    }
    updateSearch({ message: message.chat_message_id, node: null });
  };

  return (
    <div className="grid gap-4">
      {searchBar}

      <Card className="grid min-w-0 gap-0.5 px-4 py-3">
        <div className="text-[13px] font-semibold break-all">{sessionId}</div>
        <div className="flex flex-wrap gap-x-1.5 text-xs text-muted-foreground">
          {[
            displayName,
            lastActive && compactTime(lastActive),
            evaluationRuntime ? "SAMS 평가" : environment,
            stringValue(session.run_purpose, "unknown"),
            session.source_test_case_id ? `TC ${session.source_test_case_id}` : null,
            sessionType && sessionTypeLabel(sessionType),
            !messagesLoading &&
              `메시지 ${messages.length}${nextBeforeSeq !== null ? "+" : ""} 건`,
          ]
            .filter(Boolean)
            .map((part, index) => (
              <span key={index} className="flex gap-x-1.5">
                {index > 0 && <span aria-hidden>·</span>}
                <span>{part}</span>
              </span>
            ))}
        </div>
      </Card>

      {olderError && <Banner tone="error">{olderError}</Banner>}

      {/* The conversation and its trace are read TOGETHER — "which answer was
          this, and what did it cost" is one question — so an open trace sits
          BESIDE the thread rather than as a full-width card below it.

          `@container`, not a `lg:` breakpoint. The sidebar is collapsible, so
          the same 1300px window hands this page ~1000px expanded and ~1240px
          collapsed; asking the element how wide IT is makes collapsing the
          rail widen the trace — which is what a reader does when a trace
          needs room.

          Split, the columns divide by role, not by ratio: the conversation is
          capped at 32rem (a line of Korean, 60-70 characters) and the trace
          takes ALL the remainder, because width is what a waterfall and a
          token bar spend. Below the split width the page keeps one column and
          `traceColumnRef` brings the trace into view instead.

          Heights: `sticky top-4` with `max-h-[calc(100svh-2rem)]`, and each
          column its OWN scroller — `sticky` alone does not apply to the taller
          of the two, so a long trace would drag the conversation down with
          it. With no trace open the thread is the page and scrolls with it. */}
      <div className="@container min-w-0">
        <div
          className={cn(
            "grid min-w-0 items-start gap-4",
            split && "@min-[64rem]:grid-cols-[minmax(19rem,32rem)_minmax(0,1fr)]",
          )}
        >
          <div
            className={cn(
              "min-w-0",
              split &&
                "@min-[64rem]:sticky @min-[64rem]:top-4 @min-[64rem]:max-h-[calc(100svh-2rem)] @min-[64rem]:overflow-y-auto",
            )}
          >
            <ConversationThread
              messages={messages}
              loading={messagesLoading}
              selectedId={selectedId}
              assistantCount={assistantIds.length}
              clampUnselected={split}
              hasOlder={nextBeforeSeq !== null}
              olderLoading={olderLoading}
              onLoadOlder={() => void loadOlder()}
              onSelect={select}
            />
          </div>

          {/* The message's identifiers head this column rather than sitting in
              a third one: they name the answer the trace below them belongs
              to, so the two are one thing to read. */}
          {split && (
            <div
              ref={traceColumnRef}
              className="grid min-w-0 content-start gap-4 @min-[64rem]:sticky @min-[64rem]:top-4 @min-[64rem]:max-h-[calc(100svh-2rem)] @min-[64rem]:overflow-y-auto"
            >
              {!messagesLoading && (
                <MessageInspector
                  sessionId={sessionId}
                  environment={environment}
                  evaluationRuntime={evaluationRuntime}
                  sessionType={sessionType}
                  messageId={selectedId}
                  message={selectedMessage}
                  messages={messages}
                  position={position}
                  trace={trace}
                  traceLoading={traceLoading}
                  orphan={orphan}
                />
              )}

              <Card className="min-w-0 overflow-hidden">
                <CardHead title="Trace" hint="waterfall · 공유 시간축" />
                <div className="p-4">
                  {traceLoading && (
                    <p className="text-sm text-muted-foreground">트레이스 불러오는 중…</p>
                  )}
                  {/* The four failures stay four — each implies a different
                      next move (fe/CONVERSATIONS.md §1.2). */}
                  {traceError && <Banner tone="error">{traceErrorNotice(traceError)}</Banner>}
                  {trace && (
                    <TraceView
                      trace={trace}
                      title={null}
                      showMetrics={false}
                      className="mt-0 border-t-0 pt-0"
                      emptyNotice={
                        selectedMessage ? (
                          emptyTraceNotice(selectedMessage)
                        ) : (
                          <p>이 답변에 연결된 실행 기록이 없습니다.</p>
                        )
                      }
                      loadPromptVersion={(name, id) =>
                        api.getPromptVersion(name as PromptBindingName, id)
                      }
                      ioContext={{ api, environment }}
                      selectedNodeId={activeNodeId}
                      // A push, so back closes the detail. Closing is a
                      // replace so back need not step through "same node,
                      // detail closed".
                      onSelectNode={(nodeId) =>
                        updateSearch({ node: nodeId }, { replace: nodeId === null })
                      }
                    />
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
      {/* No promote button anywhere on this screen. It became the place traces
          are read; that does not make it a place cases are created (§2). */}
    </div>
  );
}
